import {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import axios from "axios"
import {Alert, Badge, Button, Card, Form, Spinner, Table} from "react-bootstrap"
import {useAuth} from "../../context/AuthProvider";

type Tenant = {
    _id: string
    describer: string
    name: {
        de: string
        en: string
    }
    fullName: {
        de: string
        en: string
    }
    isDepartment: boolean
    isInfrastructure: boolean
    isDean: boolean
}

type User = {
    _id: string
    given_name: string
    family_name: string
    email: string
    preferred_username: string
}

type TenantAccess = {
    _id: string
    role: "facultyOwner" | "deputy" | "disabilityOfficer" | "reader"
    user: User
    grantedBy?: User
    isDisabilityOfficer?: boolean
}

type Props = {
    tenantId: string
    onTenantChanged?: () => void
}

export default function TenantAccessManager({tenantId, onTenantChanged}: Props) {
    const auth = useAuth()
    const {t, i18n} = useTranslation()

    const [tenant, setTenant] = useState<Tenant | null>(null)
    const [access, setAccess] = useState<TenantAccess[]>([])
    const [candidates, setCandidates] = useState<User[]>([])
    const [selectedUserId, setSelectedUserId] = useState("")
    const [selectedRole, setSelectedRole] = useState<"reader" | "deputy" | "disabilityOfficer">("reader")
    const [selectedOwnerId, setSelectedOwnerId] = useState("")
    const [tenantFlags, setTenantFlags] = useState({
        isDepartment: false,
        isInfrastructure: false,
        isDean: false,
    })
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const lang = i18n.language.startsWith("en") ? "en" : "de"
    const maySetOwner = auth.hasAnyPermission([
        "examOfficeEmployee",
        "examOfficeAssistant",
    ])
    const mayGrantDisabilityOfficer = auth.hasAnyPermission([
        "facultyOwner",
        "examOfficeEmployee",
        "examOfficeAssistant",
    ])
    const isAdmin = auth.hasGate("admin")

    const load = async () => {
        setLoading(true)
        setError(null)

        try {
            const [tenantResponse, accessResponse, candidatesResponse] = await Promise.all([
                axios.get<Tenant>(`/api/tenant/${tenantId}`, auth.axiosAuthHeader()),
                axios.get<TenantAccess[]>(`/api/tenant/${tenantId}/access`, auth.axiosAuthHeader()),
                axios.get<User[]>(`/api/tenant/${tenantId}/candidates`, auth.axiosAuthHeader()),
            ])

            setTenant(tenantResponse.data)
            setTenantFlags({
                isDepartment: tenantResponse.data.isDepartment,
                isInfrastructure: tenantResponse.data.isInfrastructure,
                isDean: tenantResponse.data.isDean,
            })
            setAccess(accessResponse.data)
            setCandidates(candidatesResponse.data)
            setSelectedOwnerId(
                accessResponse.data.find(entry => entry.role === "facultyOwner")?.user._id ?? ""
            )
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                setError(error.response?.data?.msg ?? t("access.loadError", {ns: "tenant"}))
                return
            }

            setError(t("access.loadError", {ns: "tenant"}))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [tenantId])

    const grantAccess = async () => {
        if (!selectedUserId) return

        setSaving(true)
        setError(null)

        try {
            await axios.post(
                `/api/tenant/${tenantId}/access`,
                {
                    userId: selectedUserId,
                    role: selectedRole,
                },
                auth.axiosAuthHeader()
            )

            setSelectedUserId("")
            setSelectedRole("reader")
            await load()
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                setError(error.response?.data?.msg ?? t("access.saveError", {ns: "tenant"}))
                return
            }

            setError(t("access.saveError", {ns: "tenant"}))
        } finally {
            setSaving(false)
        }
    }

    const grantDisabilityOfficer = async (userId: string) => {
        setSaving(true)
        setError(null)

        try {
            await axios.post(
                `/api/tenant/${tenantId}/access`,
                {userId, role: "disabilityOfficer"},
                auth.axiosAuthHeader()
            )
            await load()
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("access.saveError", {ns: "tenant"})
                : t("access.saveError", {ns: "tenant"}))
        } finally {
            setSaving(false)
        }
    }

    const removeAccess = async (userId: string, role: TenantAccess["role"]) => {
        setSaving(true)
        setError(null)

        try {
            await axios.delete(
                `/api/tenant/${tenantId}/access/${userId}?role=${role}`,
                auth.axiosAuthHeader()
            )

            await load()
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                setError(error.response?.data?.msg ?? t("access.removeError", {ns: "tenant"}))
                return
            }

            setError(t("access.removeError", {ns: "tenant"}))
        } finally {
            setSaving(false)
        }
    }

    const setOwner = async () => {
        if (!selectedOwnerId) return
        setSaving(true)
        setError(null)

        try {
            await axios.put(
                `/api/tenant/${tenantId}/owner`,
                {userId: selectedOwnerId},
                auth.axiosAuthHeader()
            )
            await load()
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("access.ownerSaveError", {ns: "tenant"})
                : t("access.ownerSaveError", {ns: "tenant"}))
        } finally {
            setSaving(false)
        }
    }

    const saveTenantFlags = async () => {
        setSaving(true)
        setError(null)

        try {
            const response = await axios.patch<Tenant>(
                `/api/tenant/${tenantId}/flags`,
                tenantFlags,
                auth.axiosAuthHeader()
            )
            setTenant(response.data)
            onTenantChanged?.()
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("flags.saveError", {ns: "tenant"})
                : t("flags.saveError", {ns: "tenant"}))
        } finally {
            setSaving(false)
        }
    }

    const roleBadge = (role: TenantAccess["role"]) => {
        if (role === "facultyOwner") {
            return <Badge bg="primary">{t("roles.facultyOwner", {ns: "tenant"})}</Badge>
        }

        if (role === "deputy") {
            return <Badge bg="warning" text="dark">{t("roles.deputy", {ns: "tenant"})}</Badge>
        }

        if (role === "disabilityOfficer") {
            return <Badge bg="info" text="dark">
                {t("roles.disabilityOfficer", {ns: "tenant"})}
            </Badge>
        }

        return <Badge bg="secondary">{t("roles.reader", {ns: "tenant"})}</Badge>
    }

    if (loading) {
        return (
            <Card>
                <Card.Body className="d-flex gap-2 align-items-center">
                    <Spinner animation="border" size="sm" />
                    {t("access.loading", {ns: "tenant"})}
                </Card.Body>
            </Card>
        )
    }

    return (
        <Card>
            <Card.Header>
                <strong>{tenant?.name[lang]}</strong>
                <span className="text-muted ms-2">{tenant?.describer}</span>
            </Card.Header>

            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                {isAdmin && (
                    <Card className="mb-3" border="dark">
                        <Card.Header>
                            <strong>{t("flags.title", {ns: "tenant"})}</strong>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-flex flex-wrap gap-4 mb-3">
                                {(["isDepartment", "isInfrastructure", "isDean"] as const).map(flag => (
                                    <Form.Check
                                        key={flag}
                                        type="switch"
                                        id={`tenant-${tenantId}-${flag}`}
                                        label={t(`flags.${flag}`, {ns: "tenant"})}
                                        checked={tenantFlags[flag]}
                                        onChange={event => setTenantFlags(previous => ({
                                            ...previous,
                                            [flag]: event.target.checked,
                                        }))}
                                    />
                                ))}
                            </div>
                            <Button onClick={saveTenantFlags} disabled={saving}>
                                {t("flags.save", {ns: "tenant"})}
                            </Button>
                        </Card.Body>
                    </Card>
                )}

                {maySetOwner && (
                    <Card className="mb-3" border="primary">
                        <Card.Body>
                            <Form.Label><strong>{t("access.owner", {ns: "tenant"})}</strong></Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Select
                                    value={selectedOwnerId}
                                    onChange={event => setSelectedOwnerId(event.target.value)}
                                >
                                    <option value="">{t("access.selectOwner", {ns: "tenant"})}</option>
                                    {candidates.map(user => (
                                        <option key={user._id} value={user._id}>
                                            {user.family_name}, {user.given_name} ({user.email})
                                        </option>
                                    ))}
                                </Form.Select>
                                <Button disabled={!selectedOwnerId || saving} onClick={setOwner}>
                                    {t("access.setOwner", {ns: "tenant"})}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                )}

                <div className="d-flex gap-2 mb-3">
                    <Form.Select
                        value={selectedUserId}
                        onChange={event => setSelectedUserId(event.target.value)}
                    >
                        <option value="">{t("access.selectUser", {ns: "tenant"})}</option>

                        {candidates.filter(user =>
                            selectedRole === "disabilityOfficer"
                                ? !access.some(entry =>
                                    entry.user._id === user._id &&
                                    (entry.isDisabilityOfficer || entry.role === "disabilityOfficer")
                                )
                                : !access.some(entry => entry.user._id === user._id)
                        ).map((user: User) => (
                            <option key={user._id} value={user._id}>
                                {user.family_name}, {user.given_name} ({user.email})
                            </option>
                        ))}
                    </Form.Select>

                    <Form.Select
                        value={selectedRole}
                        onChange={event => setSelectedRole(
                            event.target.value as "reader" | "deputy" | "disabilityOfficer"
                        )}
                        style={{maxWidth: 220}}
                    >
                        <option value="reader">{t("roles.reader", {ns: "tenant"})}</option>
                        <option value="deputy">{t("roles.deputy", {ns: "tenant"})}</option>
                        <option value="disabilityOfficer">
                            {t("roles.disabilityOfficer", {ns: "tenant"})}
                        </option>
                    </Form.Select>

                    <Button
                        onClick={grantAccess}
                        disabled={!selectedUserId || saving}
                    >
                        {t("add", {ns: "common"})}
                    </Button>
                </div>

                <Table responsive hover className="mb-0">
                    <thead>
                    <tr>
                        <th>{t("name", {ns: "user"})}</th>
                        <th>{t("email", {ns: "user"})}</th>
                        <th>{t("fields.role", {ns: "tenant"})}</th>
                        <th className="text-end">{t("actions", {ns: "common"})}</th>
                    </tr>
                    </thead>

                    <tbody>
                    {access.map((entry: TenantAccess) => (
                        <tr key={entry._id}>
                            <td>{entry.user.family_name}, {entry.user.given_name}</td>
                            <td>{entry.user.email}</td>
                            <td>
                                <div className="d-flex flex-wrap align-items-center gap-1">
                                    {roleBadge(entry.role === "disabilityOfficer" ? "reader" : entry.role)}
                                    {(entry.isDisabilityOfficer || entry.role === "disabilityOfficer") &&
                                        roleBadge("disabilityOfficer")}
                                </div>
                            </td>
                            <td className="text-end">
                                {mayGrantDisabilityOfficer &&
                                !entry.isDisabilityOfficer &&
                                entry.role !== "disabilityOfficer" && (
                                    <Button
                                        className="me-1"
                                        variant="outline-info"
                                        size="sm"
                                        disabled={saving}
                                        title={t("access.addDisabilityOfficer", {ns: "tenant"})}
                                        aria-label={t("access.addDisabilityOfficer", {ns: "tenant"})}
                                        onClick={() => grantDisabilityOfficer(entry.user._id)}
                                    >
                                        <i className="bi bi-universal-access"/>
                                    </Button>
                                )}
                                {entry.role !== "disabilityOfficer" &&
                                (entry.role !== "facultyOwner" || maySetOwner) && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        disabled={saving}
                                        onClick={() => removeAccess(entry.user._id, entry.role)}
                                    >
                                        {t("remove", {ns: "common"})} {roleBadge(entry.role)}
                                    </Button>
                                )}
                                {(entry.isDisabilityOfficer || entry.role === "disabilityOfficer") && (
                                    <Button
                                        className="ms-1"
                                        variant="outline-danger"
                                        size="sm"
                                        disabled={saving}
                                        onClick={() => removeAccess(entry.user._id, "disabilityOfficer")}
                                    >
                                        {t("remove", {ns: "common"})} {roleBadge("disabilityOfficer")}
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))}

                    {access.length === 0 && (
                        <tr>
                            <td colSpan={4} className="text-muted">
                                {t("access.empty")}
                            </td>
                        </tr>
                    )}
                    </tbody>
                </Table>
            </Card.Body>
        </Card>
    )
}
